import { ReactNode, useEffect, useState } from 'react';
import { CheckIcon, Icon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MD_BREAKPOINT_MEDIA_QUERY } from '@/constants/layout';
import { usePopoverCloseSignal } from '@/stores';
import { ActiveIndicator } from '@/components/ActionBar/ActionBar.types';
import {
    PopoverRoot,
    PopoverTrigger,
    PopoverPositioner,
} from '@/components/Popover';

interface DropdownMenuItemWithPopoverProps {
    icon?: Icon;
    label: string;
    isActive?: boolean;
    activeIndicator?: ActiveIndicator;
    children: ReactNode;
    alignOffset?: number;
    disabled?: boolean;
    trailingBadge?: ReactNode;
    testId?: string;
}

export function DropdownMenuItemWithPopover({
    icon: IconComponent,
    label,
    isActive,
    activeIndicator = ActiveIndicator.CHECK,
    children,
    alignOffset = 0,
    disabled = false,
    trailingBadge,
    testId,
}: DropdownMenuItemWithPopoverProps) {
    const [isOpen, setIsOpen] = useState(false);
    const sideRoomAvailable = useMediaQuery(MD_BREAKPOINT_MEDIA_QUERY);
    const closeSignal = usePopoverCloseSignal(
        (state: { signal: number; trigger: () => void }) => state.signal,
    );

    useEffect(() => {
        if (closeSignal > 0) {
            setIsOpen(false);
        }
    }, [closeSignal]);

    const hintHoverHandlers = disabled
        ? {
              onPointerEnter: () => setIsOpen(true),
              onPointerLeave: () => setIsOpen(false),
          }
        : {};

    const handleMouseDown = (e: React.MouseEvent) => {
        if (disabled) {
            return;
        }
        e.preventDefault();
        setIsOpen(!isOpen);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((prev) => !prev);
        } else if (e.key === 'Escape' && isOpen) {
            setIsOpen(false);
        }
    };

    return (
        <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <div
                    role="menuitem"
                    tabIndex={disabled ? -1 : 0}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-disabled={disabled}
                    data-testid={testId}
                    className={cn(
                        'flex items-center gap-1.5 py-1.5 pl-2 pr-1.5 min-h-8 rounded-[5px] transition-colors group outline-none focus-visible:bg-surface-hover-strong',
                        disabled
                            ? 'cursor-default opacity-60 hover:bg-surface-hover-strong'
                            : 'cursor-pointer hover:bg-surface-hover-strong',
                    )}
                    {...hintHoverHandlers}
                    onMouseDown={handleMouseDown}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                >
                    {IconComponent && (
                        <span className="relative inline-flex size-4 flex-shrink-0 items-center justify-center">
                            <IconComponent
                                className={cn(
                                    'size-4',
                                    disabled
                                        ? 'text-grey-300'
                                        : 'text-grey-200 group-hover:text-white-100',
                                )}
                            />
                            {isActive &&
                                activeIndicator === ActiveIndicator.DOT && (
                                    <span
                                        className="pointer-events-none absolute bottom-0 right-0 w-[3px] h-[3px] rounded-full bg-white-100"
                                        aria-hidden="true"
                                    />
                                )}
                        </span>
                    )}

                    <span
                        className={cn(
                            'flex-1 min-w-0 text-[14px] leading-5 font-normal',
                            disabled ? 'text-grey-300' : 'text-white',
                        )}
                    >
                        {label}
                    </span>

                    {trailingBadge}

                    {trailingBadge === undefined &&
                        !disabled &&
                        isActive &&
                        activeIndicator === ActiveIndicator.CHECK && (
                            <CheckIcon className="size-4 text-white flex-shrink-0" />
                        )}
                </div>
            </PopoverTrigger>

            <PopoverPositioner
                side={sideRoomAvailable ? 'right' : 'bottom'}
                sideOffset={sideRoomAvailable ? 16 : 8}
                align="start"
                alignOffset={alignOffset}
                collisionPadding={16}
                {...hintHoverHandlers}
                onOpenAutoFocus={(e) => e.preventDefault()}
                data-nested-popover="true"
            >
                {children}
            </PopoverPositioner>
        </PopoverRoot>
    );
}
